import React, { useEffect, useMemo } from "react";
import HistoricoAmbiente from "../../components/HistoricoAmbiente";
import { FiCheck, FiClock, FiMinus, FiX } from "react-icons/fi";
import { diasUteis, formatarData, formatarMoeda } from "../../utils/calculos";
import { toDisplayText } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import { toDate } from "../../utils/status";
import {
  ACABAMENTO_ITEMS,
  ambientePrecisaItem,
  dataEntradaAcabamento,
  descricaoItemPendente,
  prazoAcabamento,
} from "../../utils/acabamento";
import { statusDoPrazo } from "./helpers";
import {
  ChecklistContent,
  ChecklistDisclaimer,
  ChecklistIcon,
  ChecklistList,
  ChecklistRow,
  ChecklistStatus,
  DateList,
  DateRow,
  DetailGrid,
  DetailItem,
  DrawerBody,
  DrawerClose,
  DrawerHeader,
  DrawerOverlay,
  DrawerPanel,
  DrawerSection,
  DrawerSectionTitle,
  DrawerSub,
  DrawerTitle,
  StatusBadge,
} from "./styles";

const EMPTY_VALUE = "—";
const HIDDEN_VALUE_LABEL = "******";

function buildChecklist(row) {
  // Acabamento usa um campo unico ACABAMENTO_REALIZADO (CE) para marcar fim
  // de TODOS os itens de uma vez. Quando ele esta preenchido, todos os itens
  // que precisavam ficam marcados como concluidos.
  const realizado = toDate(row?.[F.ACABAMENTO_REALIZADO]);
  return ACABAMENTO_ITEMS.map((item) => {
    const precisa = ambientePrecisaItem(row, item);
    if (!precisa) {
      return {
        id: item.id,
        label: item.label,
        descricao: null,
        tone: "default",
        icon: <FiMinus size={11} />,
        statusLabel: "Não aplicável",
        muted: true,
      };
    }
    if (realizado) {
      return {
        id: item.id,
        label: item.label,
        descricao: descricaoItemPendente(row, item),
        tone: "success",
        icon: <FiCheck size={12} />,
        statusLabel: "Concluído",
        muted: false,
      };
    }
    return {
      id: item.id,
      label: item.label,
      descricao: descricaoItemPendente(row, item),
      tone: "warning",
      icon: <FiClock size={11} />,
      statusLabel: "Pendente",
      muted: false,
    };
  });
}

export default function AmbienteDrawer({ ambiente, precosVisiveis, onClose }) {
  useEffect(() => {
    if (!ambiente) return undefined;
    function onKey(event) {
      if (event.key === "Escape") onClose?.();
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [ambiente, onClose]);

  const row = ambiente?.row;

  const checklist = useMemo(() => (row ? buildChecklist(row) : []), [row]);

  const entrada = row ? dataEntradaAcabamento(row) : null;
  const dias = entrada ? Math.round(diasUteis(entrada, new Date())) : null;
  const prazo = row ? prazoAcabamento(row) : null;
  const reprogramada = row?.[F.FINAL_ACABAMENTO_REPROGRAMADO];
  const contratoEntrega = row?.[F.ENTREGA_PREVISTA];
  const acabamentoRealizado = row?.[F.ACABAMENTO_REALIZADO];
  const prazoStatus = statusDoPrazo(prazo);

  if (!ambiente || !row) return null;

  return (
    <DrawerOverlay onMouseDown={onClose}>
      <DrawerPanel
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="acab-drawer-titulo"
      >
        <DrawerHeader>
          <div style={{ minWidth: 0, flex: 1 }}>
            <DrawerTitle id="acab-drawer-titulo">
              <span>{toDisplayText(row[F.AMBIENTE], "Ambiente não informado")}</span>
              <StatusBadge $tone={prazoStatus.statusVariant}>
                {prazoStatus.status}
              </StatusBadge>
            </DrawerTitle>
            <DrawerSub>
              <span>
                Cliente <strong>{toDisplayText(row[F.NOME_DO_CLIENTE], EMPTY_VALUE)}</strong>
              </span>
              <span>
                Pedido <strong>{toDisplayText(row[F.NUMERO_DO_PEDIDO], EMPTY_VALUE)}</strong>
              </span>
              <span>
                Lote <strong>{toDisplayText(row[F.NUMERO_DO_LOTE], EMPTY_VALUE)}</strong>
              </span>
            </DrawerSub>
          </div>
          <DrawerClose type="button" onClick={onClose} aria-label="Fechar">
            <FiX size={16} />
          </DrawerClose>
        </DrawerHeader>

        <DrawerBody>
          <DrawerSection>
            <DrawerSectionTitle>Checklist de acabamento</DrawerSectionTitle>
            <ChecklistList>
              {checklist.map((entry) => (
                <ChecklistRow key={entry.id} $tone={entry.tone} $muted={entry.muted}>
                  <ChecklistIcon $tone={entry.tone}>{entry.icon}</ChecklistIcon>
                  <ChecklistContent>
                    <strong>{entry.label}</strong>
                    {entry.descricao && <span>{entry.descricao}</span>}
                  </ChecklistContent>
                  <ChecklistStatus $tone={entry.tone}>{entry.statusLabel}</ChecklistStatus>
                </ChecklistRow>
              ))}
            </ChecklistList>
            <ChecklistDisclaimer>
              A conclusão do acabamento é registrada em uma única coluna por ambiente —
              quando ela ocorre, todos os itens esperados ficam marcados como concluídos.
            </ChecklistDisclaimer>
          </DrawerSection>

          <DrawerSection>
            <DrawerSectionTitle>Datas-chave</DrawerSectionTitle>
            <DateList>
              <DateRow>
                <span>Entrada em Acabamento</span>
                <span>{entrada ? formatarData(entrada) : EMPTY_VALUE}</span>
              </DateRow>
              <DateRow>
                <span>Dias parado</span>
                <span>{dias != null ? `${dias} du` : EMPTY_VALUE}</span>
              </DateRow>
              <DateRow>
                <span>Prazo do acabamento</span>
                <span>{formatarData(reprogramada) || EMPTY_VALUE}</span>
              </DateRow>
              <DateRow>
                <span>Entrega prevista</span>
                <span>{formatarData(contratoEntrega) || EMPTY_VALUE}</span>
              </DateRow>
              <DateRow>
                <span>Acabamento concluído</span>
                <span>{formatarData(acabamentoRealizado) || "Pendente"}</span>
              </DateRow>
            </DateList>
          </DrawerSection>

          <DrawerSection>
            <DrawerSectionTitle>Detalhes do ambiente</DrawerSectionTitle>
            <DetailGrid>
              <DetailItem>
                <span>Pedido</span>
                <span>{toDisplayText(row[F.NUMERO_DO_PEDIDO], EMPTY_VALUE)}</span>
              </DetailItem>
              <DetailItem>
                <span>Lote</span>
                <span>{toDisplayText(row[F.NUMERO_DO_LOTE], EMPTY_VALUE)}</span>
              </DetailItem>
              <DetailItem>
                <span>Marceneiro</span>
                <span>{toDisplayText(row[F.MARCENEIRO_RESPONSAVEL], EMPTY_VALUE)}</span>
              </DetailItem>
              <DetailItem>
                <span>Valor do ambiente</span>
                <span>
                  {precosVisiveis
                    ? formatarMoeda(row[F.VALOR_DO_AMBIENTE])
                    : HIDDEN_VALUE_LABEL}
                </span>
              </DetailItem>
            </DetailGrid>
          </DrawerSection>

          {/* Observação da planilha + comentários do sistema (histórico único). */}
          <HistoricoAmbiente row={row} />
        </DrawerBody>
      </DrawerPanel>
    </DrawerOverlay>
  );
}
